import React, { useCallback, useEffect, useState, useMemo } from "react";
import { buildNotasVentasParams, buildRecibosParams, calculateVentasSummaries, processFactura } from "../../print/cuadreUtils";
import { useLocation } from "react-router-dom";
import dayjs from "dayjs";
import { request } from "../../../../utils/request";
import { auth } from "../../../../utils/auth";
import { formatCurrency, formatDateTime } from "../../../../utils/helpers";

const useData = () => {
	const location = useLocation();
	const queryParams = new URLSearchParams(location.search);

	const [recibos, setRecibos] = useState([]);
	const [ordenes, setOrdenes] = useState([]);
	const [notasCredito, setNotasCredito] = useState([]);
	const [rindex, setRindex] = useState([]);
	const [summaries, setSummaries] = useState({
		efectivo: 0,
		tarjeta: 0,
		"tarjeta-debito": 0,
		transferencia: 0,
		deposito: 0,
		cheque: 0,
		otro: 0,
		pagada: 0,
		canceladas: 0,
        contado: 0,
        contraEntrega: 0,
        credito: 0,
	});

	const getOrdenes = useCallback(() => {
		const searchString =
			location.search === ""
				? "?paginate=100000"
				: location.search + "&paginate=1000000";
		const hasta = dayjs(queryParams.get("hasta")).add(1, 'days');
		request(`ordenes-compra${searchString}`).then((res) => {
			setOrdenes(res.data.map(orden => processFactura(orden, hasta)));
		});
	}, []);

	const getRecibos = useCallback(() => {
		const searchString = new URLSearchParams(buildRecibosParams(queryParams));
		request(`recibos-ingreso?${searchString.toString()}`).then((res) => {
			setRecibos(res.data);
		});
	}, []);

	const getNotasCredito = useCallback(() => {
		const searchString = new URLSearchParams(buildNotasVentasParams(queryParams));
		request(`notas-credito?${searchString.toString()}`).then((res) => {
			setNotasCredito(res.data.map(nota => {
                if (nota.estado === 'cancelada' && dayjs(nota.updated_at) > dayjs(queryParams.get("hasta"))) {
                    nota.estado = 'completada';
                }
                return nota;
            }));
		});
	}, []);

	useEffect(() => {
		const recibosIndex = [];
		recibos.forEach(recibo => recibo.cuentas.forEach(c => recibosIndex.push(c.factura_id)));
		setRindex(recibosIndex);

		const ventasSummaries = calculateVentasSummaries(ordenes, recibos);

		let canceladas = 0;
		for (const recibo of recibos) {
			if (recibo.status === "cancelado") canceladas += recibo.abono_total;
		}
		const recibosSet = new Set(recibosIndex);
		for (const orden of ordenes) {
			if (orden.estado === 'cancelada' && !recibosSet.has(orden.id)) {
				canceladas += orden.total;
			}
		}

		setSummaries({ ...ventasSummaries, canceladas });
	}, [recibos, ordenes]);

	useEffect(() => {
		getOrdenes();
		getRecibos();
        getNotasCredito();
	}, []);

	useEffect(() => {
		if (recibos.length === 0 && ordenes.length === 0) return;

		const timeout = setTimeout(() => {
			window.print();
		}, 1000);

		return () => {
			clearTimeout(timeout);
		};
	}, [recibos]);

	return useMemo(
		() => ({
			ordenes,
			recibos,
            notasCredito,
			summaries,
            rindex
		}),
		[ordenes, recibos, summaries, rindex]
	);
};

const Desktop = ({
    sucursal,
    summaries,
    desde,
    hasta,
    vendedorId,
    vendedor,
    ordenes,
    recibos,
    valorTotalRecibos,
    valorTotalNotasCredito,
    valorTotalFacturasPendientes,
    notasCredito,
    rindex,
    titulo,
}) => {
    return <div className="d-none d-md-block">
        <div className="row">
            <h2 className="col-md-12 text-center m-b-5">
                {titulo}
            </h2>
            <div className="col-md-12 text-center">
                {sucursal.nombre} - {sucursal.direccion} •{" "}
                {desde
                    ? `${desde.format(
                            "DD/MM/YYYY"
                    )} - `
                    : ""}{" "}
                {hasta.format("DD/MM/YYYY")}
            </div>
            {vendedorId && (
                <div className="col-md-12 text-center">
                    <strong className="mr-1">Vendedor:</strong> {vendedor?.nombre} {vendedor?.apellido}
                </div>
            )}
        </div>
        <div className="row mt-5">
            <div className="col-8">
                <h5 className="fw-600 w-100 text-center mb-2">
                    DETALLES DE CUADRE
                </h5>
                <table className="table m-b-40">
                    <thead>
                        <tr>
                            <th>Canceladas</th>
                            <th>Recibos</th>
                            <th>Pedidos</th>
                            <th>Notas Crédito</th>
                            <th>Total Ingresos</th>
                            <th>Total</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>
                                {formatCurrency(summaries.canceladas)}
                            </td>
                            <td>
                                {formatCurrency(
                                    valorTotalRecibos
                                )}
                            </td>
                            <td>
                                {formatCurrency(summaries.pagada)}
                            </td>
                            <td>
                                {formatCurrency(valorTotalNotasCredito)}
                            </td>
                            <td>
                                {formatCurrency(
                                    summaries.pagada + valorTotalRecibos
                                )}
                            </td>
                            <td>
                                {formatCurrency(
                                    summaries.facturado
                                )}
                            </td>
                        </tr>
                    </tbody>
                </table>

                <h5 className="fw-600 w-100 text-center mb-2">
                    FORMAS DE PAGO
                </h5>
                <table className="table m-b-40">
                    <thead>
                        <tr>
                            <th>Efectivo</th>
                            <th>Tarjeta</th>
                            <th>Transferencia</th>
                            <th>Depósito</th>
                            <th>Cheque</th>
                            <th width="90px">Otro</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>
                                {formatCurrency(summaries.efectivo)}
                            </td>
                            <td>
                                {formatCurrency(summaries.tarjeta + summaries['tarjeta-debito'])}
                            </td>
                            <td>
                                {formatCurrency(summaries.transferencia)}
                            </td>
                            <td>
                                {formatCurrency(summaries.deposito)}
                            </td>
                            <td>
                                {formatCurrency(summaries.cheque)}
                            </td>
                            <td>{formatCurrency(summaries.otro)}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <div className="col-4">
                <h5 className="fw-600 w-100 text-center mb-2">
                    TÉRMINOS DE PAGO
                </h5>
                <table className="table m-b-40 text-center" style={{tableLayout: 'fixed'}}>
                    <thead>
                        <tr>
                            <th>Contado</th>
                            <th>Crédito</th>
                            <th>Contra Entrega</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>
                                {formatCurrency(summaries.contado)}
                            </td>
                            <td>
                                {formatCurrency(summaries.credito)}
                            </td>
                            <td>
                                {formatCurrency(summaries.contraEntrega)}
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>

        <h3>PEDIDOS</h3>
        <table className="table m-b-15">
            <thead>
                <tr>
                    <th>NCF/Numero</th>
                    <th>Tipo</th>
                    <th>Cliente</th>
                    <th>Vendedor</th>
                    <th>Creación</th>
                    <th className="text-right">Vencimiento</th>
                    <th className="text-right">Término</th>
                    <th className="text-right">Tipo Pago</th>
                    <th className="text-right">Estado</th>
                    <th className="text-right">Total</th>
                    {/* <th className="text-right">Total</th> */}
                </tr>
            </thead>
            <tbody>
                {ordenes.map((factura) => {
                    const marca = rindex.indexOf(factura.id) > -1 ? '(RI)' : false;
                    const cheque = factura.cheques?.length > 0 ? factura.cheques[0] : null;
                    return (
                        <tr key={factura.id}>
                            <td>{factura.numero_comprobante}</td>
                            <td className="text-capitalize">
                                {factura.tipo}
                            </td>
                            <td>
                                {factura.cliente
                                    ? `${factura.cliente.codigo ?? ''} - ${factura.cliente.nombre}`
                                    : ""}
                                {cheque && (
                                    <div><small className="uppercase">Cheque:</small> {cheque.numero} | <small className="uppercase">Valor:</small> {formatCurrency(cheque.monto)}</div>
                                )}
                            </td>
                            <td>
                                {factura.vendedor
                                    ? `${factura.vendedor.nombre} ${factura.vendedor.apellido}`
                                    : ""}
                            </td>
                            <td>{formatDateTime(factura.created_at)}</td>
                            <td className="text-right">
                                {new Intl.DateTimeFormat("es-ES").format(
                                    new Date(factura.fecha_vencimiento)
                                )}
                            </td>
                            <td className="text-right">{factura.termino}</td>
                            <td className="text-capitalize text-right">
                                {['cancelada', 'pendiente'].indexOf(factura.estado) === -1 ? factura.tipo_pago : ''}
                            </td>
                            <td className="text-capitalize text-right">
                                <span>{factura.estado} {marca}</span>
                            </td>
                            <td className="text-right">
                                {marca || ['cancelada', 'pendiente'].indexOf(factura.estado) > -1 ? (
                                    <del>{formatCurrency(factura.total)}</del>
                                ) : formatCurrency(factura.total)}
                            </td>
                            {/* <td className="text-right">
                                {formatCurrency(factura.total)}
                            </td> */}
                        </tr>
                    );
                })}
            </tbody>
        </table>
        <div className="row m-b-40">
            <div className="col-md-12 text-right p-b-10">
                <div className="float-left">
                    TOTAL PEDIDOS: {ordenes.length}
                </div>

                <div className="float-right">
                    <h5 className="fw-600">
                        TOTAL PENDIENTES: {formatCurrency(valorTotalFacturasPendientes)} |
                        TOTAL PAGADAS: {formatCurrency(summaries.pagada)}
                    </h5>
                </div>
            </div>
        </div>

        <h3>Recibos de ingreso</h3>
        <table className="table m-b-15">
            <thead>
                <tr>
                    <th>Numero</th>
                    <th>Facturas afectadas</th>
                    <th>Cliente</th>
                    <th>Creación</th>
                    <th>Deuda</th>
                    <th>Abono</th>
                    <th>Restan</th>
                    <th>Tipo de pago</th>
                    <th>Estado</th>
                    <th></th>
                </tr>
            </thead>
            <tbody>
                {recibos.map((recibo) => {
                    const facturasAfectadas = recibo.cuentas?.map(c => {
                        return {
                            comprobante: c.factura?.numero_comprobante,
                            fecha: c.factura?.fecha,
                        }
                    });
                    return (
                        <tr key={recibo.id}>
                            <td>{recibo.numeracion}</td>
                            <td>
                                {facturasAfectadas.map(f => (
                                    <div>
                                        <div>{f.comprobante} (<small>{f.fecha}</small>)</div>
                                    </div>
                                ))}
                            </td>
                            <td>{`${recibo.cliente?.codigo ?? ''} - ${recibo.cliente?.nombre}`}</td>
                            <td>
                                {dayjs(recibo.created_at).format(
                                    "DD/MM/YYYY hh:mm"
                                )}
                            </td>
                            <td>{formatCurrency(recibo.deuda, "$")}</td>
                            <td>
                                {recibo.status === 'cancelado' ? (<del>{formatCurrency(recibo.abono_total, "$")}</del>) : (<span>{formatCurrency(recibo.abono_total, "$")}</span>)}
                            </td>
                            <td>
                                {formatCurrency(recibo.balance_total, "$")}
                            </td>
                            <td className="text-capitalize">
                                {recibo.tipo_pago}
                            </td>
                            <td className="text-capitalize">
                                {recibo.status}
                            </td>
                        </tr>
                    );
                })}
            </tbody>
        </table>
        <div className="row m-b-40">
            <div className="col-md-12 text-right p-b-10">
                <div className="float-left">
                    TOTAL RECIBOS DE INGRESO: {recibos.length}
                </div>

                <div className="float-right">
                    <h5 className="fw-600">
                        VALOR TOTAL: {formatCurrency(valorTotalRecibos)}
                    </h5>
                </div>
            </div>
        </div>

        {notasCredito.length > 0 && (
            <>
                <h3>Notas de Crédito</h3>
                <table className="table m-b-15">
                    <thead>
                        <tr>
                            <th>Numero</th>
                            <th>Facturas afectadas</th>
                            <th>Cliente</th>
                            <th>Creación</th>
                            <th>Razón</th>
                            <th>Estado</th>
                            <th className="text-right">Total</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {notasCredito.map((notaCredito) => {
                            return (
                                <tr key={notaCredito.id}>
                                    <td>{notaCredito.numeracion}</td>
                                    <td>
                                        {notaCredito.factura?.numero_comprobante}
                                    </td>
                                    <td>{`${notaCredito.contacto?.codigo ?? ''} - ${notaCredito.contacto?.nombre}`}</td>
                                    <td>
                                        {dayjs(notaCredito.created_at).format(
                                            "DD/MM/YYYY hh:mm"
                                        )}
                                    </td>
                                    <td>
                                        {notaCredito.razon}
                                    </td>
                                    <td className="text-capitalize">
                                        {notaCredito.estado}
                                    </td>
                                    <td className="text-capitalize text-right">
                                        {formatCurrency(notaCredito.total)}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
                <div className="row m-b-40">
                    <div className="col-md-12 text-right p-b-10">
                        <div className="float-left">
                            TOTAL NOTAS DE CRÉDITO: {notasCredito.length}
                        </div>

                        <div className="float-right">
                            <h5 className="fw-600">
                                VALOR TOTAL: {formatCurrency(valorTotalNotasCredito)}
                            </h5>
                        </div>
                    </div>
                </div>
            </>
        )}
    </div>;
};

const Mobile = ({
    sucursal,
    summaries,
    desde,
    hasta,
    vendedorId,
    vendedor,
    ordenes,
    recibos,
    valorTotalRecibos,
    valorTotalNotasCredito,
    valorTotalFacturasPendientes,
    notasCredito,
    rindex,
    titulo
}) => {
    return <div className="invoice invoice-mobile recibo-print d-md-none">
        <div className="clear-both p-t-30 p-b-10">
            <div>
                <h3 className="sucursal-name text-center">{titulo}</h3>
                <div className="text-center detalles-sucursal">
                    {sucursal.nombre} <br />
                    RNC: {sucursal.rnc} TEL.:{sucursal.telefono}<br />
                    {sucursal.direccion}<br />
                    {desde
                    ? `${desde.format(
                            "DD/MM/YYYY"
                        )} - `
                    : ""}{" "}
                    {hasta.format("DD/MM/YYYY")}
                </div>

                {vendedorId && (
                    <div className="detalles-sucursal text-center">
                        <strong className="mr-1">Vendedor:</strong> {vendedor?.nombre} {vendedor?.apellido}
                    </div>
                )}
            </div>
        </div>

        <div className="info-cliente-responsive d-block p-b-10">
            <hr style={{borderStyle: 'dashed', borderWidth: '1px', margin: '0'}} />
            <h5 className="text-center">DETALLES DE CUADRE</h5>
            <hr style={{borderStyle: 'dashed', borderWidth: '1px', margin: '0'}} />

            {/* <div className="d-flex">
                <div className="client-info-title">Canceladas:</div>
                <div>{formatCurrency(summaries.canceladas)}</div>
            </div>

            <div className="d-flex">
                <div className="client-info-title">Recibos:</div>
                <div>{formatCurrency(valorTotalRecibos)}</div>
            </div> */}

            {/* <div className="d-flex">
                <div className="client-info-title">Ordenes:</div>
                <div>{formatCurrency(summaries.pagada)}</div>
            </div>

            <div className="d-flex">
                <div className="client-info-title">Notas Crédito:</div>
                <div>{formatCurrency(valorTotalNotasCredito)}</div>
            </div> */}

            {/* <div className="d-flex">
                <div className="client-info-title">Total Ingresos:</div>
                <div>{formatCurrency(summaries.pagada + valorTotalRecibos)}</div>
            </div> */}

            <div className="d-flex">
                <div className="client-info-title my-auto">Total Pedido:</div>
                <div className="my-auto">{formatCurrency(summaries.facturado)}</div>
            </div>

            <div className="d-flex">
                <div className="client-info-title my-auto">Cantidad Pedidos:</div>
                <div className="my-auto">{ordenes.length}</div>
            </div>
        </div>

        <div className="info-cliente-responsive d-block p-b-10">
            <hr style={{borderStyle: 'dashed', borderWidth: '1px', margin: '0'}} />
            <h5 className="text-center">TÉRMINOS DE PAGO</h5>
            <hr style={{borderStyle: 'dashed', borderWidth: '1px', margin: '0'}} />

            <div className="d-flex">
                <div className="client-info-title my-auto">Contado:</div>
                <div className="my-auto">{formatCurrency(summaries.contado)}</div>
            </div>

            <div className="d-flex">
                <div className="client-info-title my-auto">Crédito:</div>
                <div className="my-auto">{formatCurrency(summaries.credito)}</div>
            </div>

            <div className="d-flex">
                <div className="client-info-title my-auto">Contra Entrega:</div>
                <div className="my-auto">{formatCurrency(summaries.contraEntrega)}</div>
            </div>
        </div>

        <div className="info-cliente-responsive d-block p-b-10">
            <hr style={{borderStyle: 'dashed', borderWidth: '1px', margin: '0'}} />
            <h5 className="text-center">FORMAS DE PAGO</h5>
            <hr style={{borderStyle: 'dashed', borderWidth: '1px', margin: '0'}} />

            <div className="d-flex">
                <div className="client-info-title my-auto">Efectivo:</div>
                <div className="my-auto">{formatCurrency(summaries.efectivo)}</div>
            </div>

            <div className="d-flex">
                <div className="client-info-title my-auto">Tarjeta:</div>
                <div className="my-auto">{formatCurrency(summaries.tarjeta + summaries['tarjeta-debito'])}</div>
            </div>

            <div className="d-flex">
                <div className="client-info-title my-auto">Transferencia:</div>
                <div className="my-auto">{formatCurrency(summaries.transferencia)}</div>
            </div>

            <div className="d-flex">
                <div className="client-info-title my-auto">Depósito:</div>
                <div className="my-auto">{formatCurrency(summaries.deposito)}</div>
            </div>

            <div className="d-flex">
                <div className="client-info-title my-auto">Cheque:</div>
                <div className="my-auto">{formatCurrency(summaries.cheque)}</div>
            </div>

            <div className="d-flex">
                <div className="client-info-title my-auto">Otro:</div>
                <div className="my-auto">{formatCurrency(summaries.otro)}</div>
            </div>
        </div>

        {ordenes?.length > 0 && <>
            <hr style={{borderStyle: 'dashed', borderWidth: '1px', margin: '0'}} />
            <div className="d-block bold tipo-factura text-center uppercase">
                Pedidos
            </div>
            <hr style={{borderStyle: 'dashed', borderWidth: '1px', margin: '0'}} />
            <div>
                <div className="row tabla-titulos" style={{ fontWeight: "600" }}>
                    <div className="col-5">
                        <small>Detalle</small>
                    </div>
                    <div className="col-4" style={{paddingRight: '5px'}}>
                        <small></small>
                    </div>
                    <div className="col-3 text-right" style={{ whiteSpace: "nowrap" }}>
                        <small>PAGO</small>
                    </div>
                </div>
                <hr style={{borderStyle: 'dashed', borderWidth: '1px', margin: '0 0 5px'}} />

                {ordenes.map((factura) => {
                    const marca = rindex.indexOf(factura.id) > -1 ? '(RI)' : false;
                    const cheque = factura.cheques?.length > 0 ? factura.cheques[0] : null;

                    return <div className="row producto-item align-items-end" style={{ marginBottom: "5px" }} key={factura.id}>
                        <div className="col-5" style={{ lineHeight: "1" }}>
                            <strong>{factura.numero_comprobante}</strong><br/>
                            {factura.cliente ? `${factura.cliente.codigo ?? ''} - ${factura.cliente.nombre}` : ""} <br/>
                            Tipo: {factura.tipo} <br/>
                            Estado: <span>{factura.estado} {marca}</span>
                        </div>
                        <div className="col-4" style={{ lineHeight: "1", fontSize: '12px' }}>
                            C: {new Intl.DateTimeFormat("es-ES").format(new Date(factura.created_at))}<br/>
                            V: {new Intl.DateTimeFormat("es-ES").format(new Date(factura.fecha_vencimiento))}
                        </div>
                        <div className="col-3" style={{ lineHeight: "1" }}>
                            {factura.termino}<br/>
                            {marca || ['cancelada', 'pendiente'].indexOf(factura.estado) > -1 ? (<del>{formatCurrency(factura.total)}</del>) : formatCurrency(factura.total)}<br/>
                            {['cancelada', 'pendiente'].indexOf(factura.estado) === -1 ? <>{factura.tipo_pago}<br/></> : ''}
                            {cheque && <div><small className="uppercase">Cheque:</small> {cheque.numero} | <small className="uppercase">Valor:</small> {formatCurrency(cheque.monto)}</div>}
                        </div>
                    </div>;
                })}
            </div>
            <hr className="mt-2" style={{borderStyle: 'dashed', borderWidth: '1px'}} />
            <div className="totales">
                <div className="row justify-content-between justify-content-md-end">
                    <div className="col-8" style={{ fontWeight: "600" }}>TOTAL PEDIDOS</div>
                    <div className="col-4 text-right">{ordenes.length}</div>

                    {/* <div className="col-8" style={{ fontWeight: "600" }}>TOTAL PENDIENTES</div>
                    <div className="col-4 text-right">{formatCurrency(valorTotalFacturasPendientes)}</div> */}

                    <div className="col-8" style={{ fontWeight: "600" }}>VALOR TOTAL</div>
                    <div className="col-4 text-right">{formatCurrency(summaries.pagada)}</div>
                </div>
            </div>
        </>}

        {recibos?.length > 0 && <>
            <hr style={{borderStyle: 'dashed', borderWidth: '1px', margin: '0'}} />
            <div className="d-block bold tipo-factura text-center uppercase">
                Recibos de ingreso
            </div>
            <hr style={{borderStyle: 'dashed', borderWidth: '1px', margin: '0'}} />
            <div>
                <div className="row tabla-titulos" style={{ fontWeight: "600" }}>
                    <div className="col-5">
                        <small>Detalle</small>
                    </div>
                    <div className="col-4" style={{paddingRight: '5px'}}>
                        <small></small>
                    </div>
                    <div className="col-3 text-right" style={{ whiteSpace: "nowrap" }}>
                        <small>PAGO</small>
                    </div>
                </div>
                <hr style={{borderStyle: 'dashed', borderWidth: '1px', margin: '0 0 5px'}} />

                {recibos.map((recibo) => {
                    const facturasAfectadas = recibo.cuentas?.map(c => c.factura?.numero_comprobante);

                    return <div className="row producto-item align-items-end" style={{ marginBottom: "5px" }} key={recibo.id}>
                        <div className="col-5" style={{ lineHeight: "1" }}>
                            <strong>{recibo.numeracion}</strong><br/>
                            {recibo.cliente ? `${recibo.cliente.codigo ?? ''} - ${recibo.cliente.nombre}` : ""}<br/>
                            A: {facturasAfectadas.join('; ')}<br/>
                            Estado: <span>{recibo.status}</span>
                        </div>
                        <div className="col-4" style={{ lineHeight: "1", fontSize: '12px' }}>
                            C: {dayjs(recibo.created_at).format("DD/MM/YYYY")}<br/>
                            Deuda: {formatCurrency(recibo.deuda, "$")}
                        </div>
                        <div className="col-3" style={{ lineHeight: "1" }}>
                            {recibo.status === 'cancelado' ? (<del>{formatCurrency(recibo.abono_total, "$")}</del>) : (<span>{formatCurrency(recibo.abono_total, "$")}</span>)}<br/>
                            {recibo.tipo_pago}<br/>
                            Restan: {formatCurrency(recibo.balance_total, "$")}
                        </div>
                    </div>;
                })}
            </div>
            <hr className="mt-2" style={{borderStyle: 'dashed', borderWidth: '1px'}} />
            <div className="totales">
                <div className="row justify-content-between justify-content-md-end">
                    <div className="col-8" style={{ fontWeight: "600" }}>TOTAL RECIBOS</div>
                    <div className="col-4 text-right">{recibos.length}</div>

                    <div className="col-8" style={{ fontWeight: "600" }}>VALOR TOTAL</div>
                    <div className="col-4 text-right">{formatCurrency(valorTotalRecibos)}</div>
                </div>
            </div>
        </>}

        {notasCredito?.length > 0 && <>
            <hr style={{borderStyle: 'dashed', borderWidth: '1px', margin: '0'}} />
            <div className="d-block bold tipo-factura text-center uppercase">
                Notas de Crédito
            </div>
            <hr style={{borderStyle: 'dashed', borderWidth: '1px', margin: '0'}} />
            <div>
                <div className="row tabla-titulos" style={{ fontWeight: "600" }}>
                    <div className="col-5">
                        <small>Detalle</small>
                    </div>
                    <div className="col-4" style={{paddingRight: '5px'}}>
                        <small></small>
                    </div>
                    <div className="col-3 text-right" style={{ whiteSpace: "nowrap" }}>
                        <small>PAGO</small>
                    </div>
                </div>
                <hr style={{borderStyle: 'dashed', borderWidth: '1px', margin: '0 0 5px'}} />

                {notasCredito.map((notaCredito) => {
                    return <div className="row producto-item align-items-end" style={{ marginBottom: "5px" }} key={notaCredito.id}>
                        <div className="col-5" style={{ lineHeight: "1" }}>
                            <strong>{notaCredito.numeracion}</strong><br/>
                            {notaCredito.contacto ? `${notaCredito.contacto.codigo ?? ''} - ${notaCredito.contacto.nombre}` : ""}<br/>
                            A: {notaCredito.factura?.numero_comprobante}
                        </div>
                        <div className="col-4" style={{ lineHeight: "1", fontSize: '12px' }}>
                            C: {dayjs(notaCredito.created_at).format("DD/MM/YYYY")}<br/>
                            Razón: {notaCredito.razon}
                        </div>
                        <div className="col-3" style={{ lineHeight: "1" }}>
                            {formatCurrency(notaCredito.total)}<br/>
                            Estado: <span>{notaCredito.estado}</span>
                        </div>
                    </div>;
                })}
            </div>
            <hr className="mt-2" style={{borderStyle: 'dashed', borderWidth: '1px'}} />
            <div className="totales">
                <div className="row justify-content-between justify-content-md-end">
                    <div className="col-8" style={{ fontWeight: "600" }}>TOTAL NOTAS DE CRÉDITO</div>
                    <div className="col-4 text-right">{notasCredito.length}</div>

                    <div className="col-8" style={{ fontWeight: "600" }}>VALOR TOTAL</div>
                    <div className="col-4 text-right">{formatCurrency(valorTotalNotasCredito)}</div>
                </div>
            </div>
        </>}

        <div className="my-3"></div>
    </div>;
};

const ReporteCuadreCaja = () => {
    const location = useLocation();
	const queryParams = new URLSearchParams(location.search);
    const desde = dayjs(queryParams.get("desde"));
    const hasta = dayjs(queryParams.get("hasta"));
    const tipo = dayjs(queryParams.get("tipo"));

    const titulo = tipo === 'cliente' ? 'Reporte Pedidos - Cuadre de Caja' : 'Reporte Ordenes - Cuadre de Caja';
	const { ordenes, recibos, notasCredito, summaries, rindex } = useData();

	const user = auth.getUser();
	const sucursal = user.sucursales.filter(
		(suc) => suc.id === user.sucursalActual
	)[0];

	const valorTotalFacturasPendientes = ordenes.reduce((acc, factura) => {
		return (
			acc +
			(factura.estado === "pendiente" ? parseFloat(factura.por_cobrar) : 0)
		);
	}, 0);
	const valorTotalRecibos = recibos.reduce((acc, recibo) => {
		return (
			acc +
			(recibo.status === "cancelado" ? 0 : parseFloat(recibo.abono_total))
		);
	}, 0);
    const valorTotalNotasCredito = notasCredito.reduce((acc, notaCredito) => {
		return (
			acc +
			(notaCredito.estado === "cancelada" ? 0 : parseFloat(notaCredito.total))
		);
	}, 0);

    const vendedorId = queryParams.get("vendedor");
    let vendedor = {};
    if (vendedorId) {
        vendedor = ordenes.find((factura) => factura.vendedor?.id === parseInt(vendedorId))?.vendedor;
    }

    return <>
		<style>
			{`
			@import url('https://fonts.googleapis.com/css2?family=Saira+Extra+Condensed:wght@400;700&display=swap');
			@media (max-width: 450px) {
				body {
					font-family: "Saira Extra Condensed", sans-serif !important;
					width: 100% !important;
					font-size: 12pt !important;
				}

				@media print {
					body {
						min-width: auto !important;
					}
				}

				@page {
					size: 80mm auto;
					margin: 0;
				}

				@page {
					size: 80mm 300mm !important;
					margin-top: 0 !important;
					margin-bottom: 0 !important;
					margin-left: 20px !important;
					margin-right: 20px !important;
				}

				.invoice {
					width: 100% !important;
					padding: 0 20px 0 5px;
				}
				.invoice small {
					font-size: 10pt;
					line-height: 1;
				}
				.invoice-table {
					display: none;
				}
				.sm-invoice-table {
					display: block;
				}
				.invoice h3 {
					margin-bottom: 0.1rem;
				}
				.invoice h3.signature-title {
					font-weight: 600 !important;
				}
				.client-info-title {
					font-weight: 600 !important;
				}
				.info-cliente-responsive .info-client__response {
					line-height: 1.2 !important;
				}
				.invoice-name {
					font-weight: 600 !important;
				}
				.info-cliente-responsive {
					line-height: 1.2 !important;
					margin-bottom: 5px !important;
				}
				.info-cliente-responsive .row{
					margin-bottom: 6px;
				}
				.invoice-mobile {
					display: block;
					width: 100% !important;
				}
				hr {
					margin: 5px 0;
				}
				.sucursal-name {
					font-size: 14pt;
				}
				.detalles-sucursal {
					line-height: 1;
					margin-bottom: 10px;
				}
				.top-info {
					line-height: 1;
				}
				.sucursal-name-sm {
					display:block;
				}
				.producto-item {
					font-size: 10pt;
				}
				.factura-logo{
					display:none;
				}
				.sucursal-name-lg{
					display:none;
				}
				.info-cliente-responsive .row {
					margin-bottom: 0 !important;
				}
				.client-info-title {
					min-width: 90px;
				}
				.tabla-titulos small {
					font-weight: 600;
				}
				.totales {
					line-height: 1.2;
					margin-bottom: 10px;
				}
				.summary-text{
					// max-width:39% !important;
					width:auto !important;
					flex:auto;
					padding-left: 5px !important;
					padding-right: 2px !important;
				}
				.justify-content-between {
					justify-content: flex-end !important;
				}
				.justify-content-between :nth-child(2){
					max-width: 33% !important;
				}
			}`}
		</style>
        
        <Desktop sucursal={sucursal}
            summaries={summaries}
            desde={desde}
            hasta={hasta}
            vendedorId={vendedorId}
            vendedor={vendedor}
            recibos={recibos}
            ordenes={ordenes}
            valorTotalRecibos={valorTotalRecibos}
            valorTotalNotasCredito={valorTotalNotasCredito}
            valorTotalFacturasPendientes={valorTotalFacturasPendientes}
            notasCredito={notasCredito}
            rindex={rindex}
            titulo={titulo}
        />

        <Mobile sucursal={sucursal}
            summaries={summaries}
            desde={desde}
            hasta={hasta}
            vendedorId={vendedorId}
            vendedor={vendedor}
            recibos={recibos}
            ordenes={ordenes}
            valorTotalRecibos={valorTotalRecibos}
            valorTotalNotasCredito={valorTotalNotasCredito}
            valorTotalFacturasPendientes={valorTotalFacturasPendientes}
            notasCredito={notasCredito}
            rindex={rindex}
            titulo={titulo}
        />
    </>;
};

export default ReporteCuadreCaja;
